import { useCallback } from "react";

import { useDispatch } from "react-redux";

import { useRequestWithFeedback } from "../../../composites";
import { useObjectMemo } from "../../../hooks";
import { metadataActions } from "../subscriptionMetadata/slice";

import { actions as entityActions } from "./slice";
import SubscriptionsAPI from "./api";
import { calculateEstimatedYearlySavings } from "./utils";

import type { QueryParams, SubscriptionsPerCardResponse } from "./types";
import type { OverrideRunProps } from "../../../composites";

export const useSubscriptionRequests = () => {
  const dispatch = useDispatch();
  const { send, loading } =
    useRequestWithFeedback<SubscriptionsPerCardResponse>();

  const fetchCardSubscriptions = useCallback(
    (
      cardId: API.CardId,
      dateFilters?: QueryParams,
      {
        onData: overrideOnData,
        ...restRunProps
      }: OverrideRunProps<SubscriptionsPerCardResponse> = {},
    ) => {
      send({
        action: SubscriptionsAPI.getSubscriptionsForCard(cardId, dateFilters),
        onData(data) {
          const { monthly_transactions: monthlyTransactions, subscriptions } =
            data;

          // Retain only the last 12 months. Then, for each transaction:
          //  • compute and attach estimated_savings
          const modifiedTransactions: API.CardTransactionsPerMonth[] =
            monthlyTransactions.slice(-12);

          const subscriptionsWithSavings: API.CardSubscription[] =
            subscriptions.map((subscription) => ({
              ...subscription,
              estimated_savings: calculateEstimatedYearlySavings(
                subscription.amount,
                subscription.recurrence_frequency,
              ),
            }));

          // Dispatch subscription metadata separately
          dispatch(metadataActions.setAll(subscriptionsWithSavings));

          dispatch(
            entityActions.setOne({
              card_id: cardId,
              data: modifiedTransactions,
            }),
          );

          overrideOnData?.({
            monthly_transactions: modifiedTransactions,
            subscriptions: subscriptionsWithSavings,
          });
        },
        onError() {
          dispatch(entityActions.flush(cardId));
          dispatch(metadataActions.removeAll());
        },
        ...restRunProps,
      });
    },
    [dispatch, send],
  );

  return useObjectMemo({ send: fetchCardSubscriptions, loading });
};
